/**
 * eBay listing description templating.
 *
 * eBay item descriptions are HTML. This renders one from a template plus the
 * details we already hold for a product — including everything pulled in when
 * the product was synced from Amazon.
 *
 * The template is a plain HTML string with {{token}} placeholders. Users can
 * override DEFAULT_TEMPLATE from Settings; the override lives in localStorage.
 *
 * eBay's active-content rules ban JavaScript, Flash and forms in descriptions,
 * but allow CSS — so a <style> block with media queries is fine here.
 */

const TEMPLATE_STORAGE_KEY = "ebay_description_template";
const PRESENTATION_STORAGE_KEY = "ebay_description_presentation";

/**
 * Presentation settings shared by every listing — the parts a seller sets once
 * rather than per item.
 */
export const DEFAULT_PRESENTATION = {
  bannerUrl: "",
  bannerAlt: "",
  handlingDays: "1",
  shippingService: "USPS Ground Advantage with tracking",
  returnTerms: "30-day returns accepted. Buyer pays return shipping.",
  packagingLines: [
    "Carefully packaged to protect the item in transit",
    "Shipped in a sturdy mailer or box appropriate to the item",
  ].join("\n"),
};

/**
 * Default description template.
 *
 * Structure and styling follow the seller's house layout: banner, title block,
 * condition callout, spec table, highlights, what-you-get, detail photos and
 * terms. Every section collapses to nothing when its data is absent, so the
 * same template works for a trading card and for an Amazon-sourced product.
 */
export const DEFAULT_TEMPLATE = `
<style type="text/css">
  @media only screen and (max-width: 600px) {
    .ls-pad { padding: 18px !important; }
    .ls-h1 { font-size: 22px !important; }
    .ls-spec-label { display: block !important; width: auto !important; padding-bottom: 2px !important; }
    .ls-spec-value { display: block !important; width: auto !important; padding-bottom: 12px !important; }
  }
</style>

<div style="max-width:1000px;margin:0 auto;background:#ffffff;color:#14171a;font-family:Helvetica,Arial,sans-serif;font-size:15px;line-height:1.6;">

  {{bannerHtml}}

  <!-- TITLE -->
  <div class="ls-pad" style="padding:32px 28px 24px;border-bottom:1px solid #dcdcd6;">
    {{eyebrowHtml}}
    <h1 class="ls-h1" style="font-family:Georgia,'Times New Roman',serif;font-size:28px;line-height:1.25;font-weight:normal;margin:0 0 12px;color:#14171a;">
      {{title}}{{subtitleHtml}}
    </h1>
    <div style="margin:0;color:#6d6f72;font-size:16px;">{{description}}</div>
  </div>

  <!-- CONDITION -->
  <div class="ls-pad" style="padding:24px 28px;background:#f4f4f2;border-left:4px solid #1f4d5c;">
    <div style="font-family:Consolas,Menlo,Monaco,monospace;font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:#1f4d5c;margin:0 0 8px;">
      Condition
    </div>
    <div style="font-weight:bold;margin:0 0 6px;">{{conditionGrade}}</div>
    <p style="margin:0;color:#3d4045;">{{conditionDetail}}</p>
  </div>

  {{specsHtml}}

  {{bulletsHtml}}

  {{whatYouGetHtml}}

  {{detailImagesHtml}}

  <!-- TERMS -->
  <div class="ls-pad" style="padding:28px;border-top:1px solid #dcdcd6;background:#f4f4f2;margin-top:20px;">
    <table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;font-size:14px;">
      <tr>
        <td class="ls-spec-label" style="width:38%;padding:10px 12px 10px 0;vertical-align:top;font-family:Consolas,Menlo,Monaco,monospace;font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:#1f4d5c;">Shipping</td>
        <td class="ls-spec-value" style="width:62%;padding:10px 0;vertical-align:top;color:#3d4045;">Ships within {{handlingTime}}. {{shippingService}}.</td>
      </tr>
      <tr>
        <td class="ls-spec-label" style="width:38%;padding:10px 12px 10px 0;vertical-align:top;font-family:Consolas,Menlo,Monaco,monospace;font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:#1f4d5c;">Returns</td>
        <td class="ls-spec-value" style="width:62%;padding:10px 0;vertical-align:top;color:#3d4045;">{{returnTerms}}</td>
      </tr>
      <tr>
        <td class="ls-spec-label" style="width:38%;padding:10px 12px 10px 0;vertical-align:top;font-family:Consolas,Menlo,Monaco,monospace;font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:#1f4d5c;">Questions</td>
        <td class="ls-spec-value" style="width:62%;padding:10px 0;vertical-align:top;color:#3d4045;">Send a message through eBay and I'll get back to you quickly.</td>
      </tr>
    </table>
  </div>

</div>
`.trim();

/** Headline grade shown in the condition callout. */
const CONDITION_GRADE = {
  new: "New — unused, in original packaging",
  like_new: "Like New / Open Box",
  good: "Used — Good",
  fair: "Used — Acceptable",
  poor: "For Parts or Not Working",
};

/** Supporting sentence beneath the grade. */
const CONDITION_DETAIL = {
  new: "Brand new and unused. Photos show the exact item you will receive.",
  like_new:
    "Opened but unused, with no visible wear. Inspected and complete. Photos show the exact item you will receive.",
  good: "Used with light cosmetic wear and fully functional. Photos show the exact item you will receive.",
  fair: "Used with noticeable cosmetic wear. Functions as intended. Photos show the exact item you will receive.",
  poor: "Sold as-is for parts or repair. Please review the photos carefully before purchasing.",
};

/** Escape a value for safe interpolation into HTML text/attributes. */
export function escapeHtml(value) {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** The active template — the user's override if set, otherwise the default. */
export function getTemplate() {
  return localStorage.getItem(TEMPLATE_STORAGE_KEY) || DEFAULT_TEMPLATE;
}

export function saveTemplate(html) {
  localStorage.setItem(TEMPLATE_STORAGE_KEY, html);
}

export function resetTemplate() {
  localStorage.removeItem(TEMPLATE_STORAGE_KEY);
}

export function hasCustomTemplate() {
  return !!localStorage.getItem(TEMPLATE_STORAGE_KEY);
}

/** Presentation settings, merged over the defaults. */
export function getPresentation() {
  try {
    const stored = JSON.parse(
      localStorage.getItem(PRESENTATION_STORAGE_KEY) || "{}",
    );
    return { ...DEFAULT_PRESENTATION, ...stored };
  } catch (err) {
    return { ...DEFAULT_PRESENTATION };
  }
}

export function savePresentation(presentation) {
  localStorage.setItem(
    PRESENTATION_STORAGE_KEY,
    JSON.stringify({ ...getPresentation(), ...presentation }),
  );
}

export function resetPresentation() {
  localStorage.removeItem(PRESENTATION_STORAGE_KEY);
}

/** Section heading in the small uppercase monospace style. */
function sectionLabel(text, marginBottom = "12px") {
  return `<div style="font-family:Consolas,Menlo,Monaco,monospace;font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:#1f4d5c;margin:0 0 ${marginBottom};">
      ${escapeHtml(text)}
    </div>`;
}

/** Banner image across the top. Returns "" when no banner is configured. */
function buildBannerHtml(bannerUrl, bannerAlt) {
  if (!bannerUrl) return "";
  return `<img src="${escapeHtml(bannerUrl)}" alt="${escapeHtml(bannerAlt || "")}" style="display:block;width:100%;max-width:1000px;height:auto;border:0;margin:0;" />`;
}

/** Category eyebrow above the title. */
function buildEyebrowHtml(eyebrow) {
  if (!eyebrow) return "";
  return sectionLabel(eyebrow, "10px");
}

/** Secondary line inside the title block. */
function buildSubtitleHtml(subtitle) {
  if (!subtitle) return "";
  return `<br />
      <span style="font-size:20px;color:#3d4045;">${escapeHtml(subtitle)}</span>`;
}

/**
 * Two-column spec table.
 *
 * @param {Array<{label: string, value: string}>} specs
 */
function buildSpecsHtml(specs = []) {
  const rows = specs.filter((spec) => spec && spec.label && spec.value);
  if (rows.length === 0) return "";

  const cells = rows
    .map(
      (spec) => `<tr>
        <td class="ls-spec-label" style="width:38%;padding:10px 12px 10px 0;border-bottom:1px dotted #dcdcd6;color:#6d6f72;vertical-align:top;">${escapeHtml(spec.label)}</td>
        <td class="ls-spec-value" style="width:62%;padding:10px 0;border-bottom:1px dotted #dcdcd6;vertical-align:top;">${escapeHtml(spec.value)}</td>
      </tr>`,
    )
    .join("\n      ");

  return `<div class="ls-pad" style="padding:32px 28px 8px;">
    ${sectionLabel("Item details", "16px")}
    <table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;font-size:15px;">
      ${cells}
    </table>
  </div>`;
}

/** Highlights list built from feature bullets. */
function buildBulletsHtml(bullets = []) {
  const items = bullets.filter(Boolean);
  if (items.length === 0) return "";

  const list = items
    .map((b) => `<li style="margin:0 0 6px;">${escapeHtml(b)}</li>`)
    .join("\n      ");

  return `<div class="ls-pad" style="padding:28px 28px 8px;">
    ${sectionLabel("Highlights")}
    <ul style="margin:0;padding:0 0 0 20px;">
      ${list}
    </ul>
  </div>`;
}

/** "What you get" list: the item itself plus the seller's packaging notes. */
function buildWhatYouGetHtml(title, packagingLines = "") {
  const lines = String(packagingLines)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const leadIn = title
    ? `<li style="margin:0 0 6px;">1&times; ${escapeHtml(title)} — the exact item pictured</li>`
    : "";

  if (!leadIn && lines.length === 0) return "";

  const list = [leadIn]
    .concat(
      lines.map(
        (line) => `<li style="margin:0 0 6px;">${escapeHtml(line)}</li>`,
      ),
    )
    .filter(Boolean)
    .join("\n      ");

  return `<div class="ls-pad" style="padding:28px 28px 8px;">
    ${sectionLabel("What you get")}
    <ul style="margin:0;padding:0 0 0 20px;">
      ${list}
    </ul>
  </div>`;
}

/**
 * Large stacked detail photos.
 *
 * eBay already shows the gallery images above the description, so this skips
 * the first photo and only renders the additional angles.
 */
function buildDetailImagesHtml(images = []) {
  const extras = images.slice(1);
  if (extras.length === 0) return "";

  const blocks = extras
    .map(
      (url) =>
        `<img src="${escapeHtml(url)}" alt="" style="display:block;width:100%;max-width:100%;height:auto;border:1px solid #dcdcd6;margin:0 0 20px;" />`,
    )
    .join("\n    ");

  return `<div class="ls-pad" style="padding:28px 28px 8px;">
    ${sectionLabel("Detail")}
    ${blocks}
  </div>`;
}

/** "1 business day" vs "3 business days" — a bare number reads wrong at 1. */
function formatHandlingTime(handlingDays) {
  const raw = String(handlingDays ?? "").trim();
  if (!raw) return "1 business day";
  return `${raw} business ${parseFloat(raw) === 1 ? "day" : "days"}`;
}

/** Wrap plain text in paragraphs; pass through anything already HTML. */
function toParagraphs(text) {
  if (/<[a-z][\s\S]*>/i.test(text || "")) return text;
  return escapeHtml(text || "")
    .split(/\n{2,}/)
    .filter(Boolean)
    .map((p) => `<p style="margin:0 0 12px;">${p.replace(/\n/g, "<br />")}</p>`)
    .join("\n    ");
}

/**
 * Render a description.
 *
 * @param {Object} details - Output of buildListingDetails() in services/amazon.js
 * @param {Object} options
 * @param {string} options.template - Template override for this render
 * @param {Object} options.presentation - Presentation override for this render
 * @returns {string} HTML description
 */
export function renderDescription(details = {}, options = {}) {
  const {
    title = "",
    subtitle = "",
    eyebrow = "",
    description = "",
    condition = "new",
    conditionGrade,
    conditionDetail,
    price = "",
    sku = "",
    brand = "",
    images = [],
    bullets = [],
    specs = [],
    sourceUrl = "",
  } = details;

  const template = options.template || getTemplate();
  const presentation = {
    ...getPresentation(),
    ...(options.presentation || {}),
  };

  const values = {
    title: escapeHtml(title),
    subtitle: escapeHtml(subtitle),
    subtitleHtml: buildSubtitleHtml(subtitle),
    eyebrow: escapeHtml(eyebrow),
    eyebrowHtml: buildEyebrowHtml(eyebrow),
    description: toParagraphs(description),
    condition: escapeHtml(condition),
    conditionGrade: escapeHtml(
      conditionGrade || CONDITION_GRADE[condition] || CONDITION_GRADE.new,
    ),
    conditionDetail: escapeHtml(
      conditionDetail || CONDITION_DETAIL[condition] || CONDITION_DETAIL.new,
    ),
    // Kept for templates written against the earlier token set.
    conditionText: escapeHtml(
      conditionDetail || CONDITION_DETAIL[condition] || CONDITION_DETAIL.new,
    ),
    price: escapeHtml(price),
    sku: escapeHtml(sku),
    brand: escapeHtml(brand),
    sourceUrl: escapeHtml(sourceUrl),
    bannerHtml: buildBannerHtml(presentation.bannerUrl, presentation.bannerAlt),
    bannerUrl: escapeHtml(presentation.bannerUrl),
    specsHtml: buildSpecsHtml(specs),
    bulletsHtml: buildBulletsHtml(bullets),
    whatYouGetHtml: buildWhatYouGetHtml(title, presentation.packagingLines),
    detailImagesHtml: buildDetailImagesHtml(images),
    imagesHtml: buildDetailImagesHtml(images),
    handlingDays: escapeHtml(presentation.handlingDays),
    handlingTime: escapeHtml(formatHandlingTime(presentation.handlingDays)),
    shippingService: escapeHtml(presentation.shippingService),
    returnTerms: escapeHtml(presentation.returnTerms),
  };

  // Unknown tokens collapse to "" so a template can reference optional fields.
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) =>
    Object.prototype.hasOwnProperty.call(values, token) ? values[token] : "",
  );
}

/** Tokens a template may reference, for the Settings help text. */
export const TEMPLATE_TOKENS = [
  { token: "{{title}}", description: "Listing title" },
  { token: "{{subtitleHtml}}", description: "Secondary title line block" },
  {
    token: "{{eyebrowHtml}}",
    description: "Small uppercase category label block",
  },
  { token: "{{description}}", description: "Lead paragraphs (HTML-safe)" },
  {
    token: "{{conditionGrade}}",
    description: "Condition headline, e.g. Like New",
  },
  {
    token: "{{conditionDetail}}",
    description: "Condition explanation sentence",
  },
  { token: "{{bannerHtml}}", description: "Store banner image block" },
  { token: "{{specsHtml}}", description: "Item details spec table block" },
  { token: "{{bulletsHtml}}", description: "Highlights list block" },
  { token: "{{whatYouGetHtml}}", description: "What-you-get list block" },
  {
    token: "{{detailImagesHtml}}",
    description: "Large detail photos (skips the gallery image)",
  },
  { token: "{{handlingDays}}", description: "Handling time, bare number" },
  {
    token: "{{handlingTime}}",
    description: 'Handling time, pluralised — e.g. "2 business days"',
  },
  { token: "{{shippingService}}", description: "Shipping service description" },
  { token: "{{returnTerms}}", description: "Return policy wording" },
  { token: "{{price}}", description: "Listing price" },
  { token: "{{sku}}", description: "SKU / ASIN" },
  { token: "{{brand}}", description: "Brand, when known" },
  {
    token: "{{sourceUrl}}",
    description:
      "Original product URL — leave out of live listings; eBay bars links to other marketplaces",
  },
];

const ebayDescription = {
  DEFAULT_TEMPLATE,
  DEFAULT_PRESENTATION,
  TEMPLATE_TOKENS,
  getTemplate,
  saveTemplate,
  resetTemplate,
  hasCustomTemplate,
  getPresentation,
  savePresentation,
  resetPresentation,
  renderDescription,
  escapeHtml,
};

export default ebayDescription;
